import type { FC, ReactNode } from 'react'

import { RuleDraw } from '@/components/generals/rule-draw'
import { useReveal } from '@/hooks/use-reveal'

import { MICRO_FAINT } from '@/constants/type'
import { COLOPHON } from '@/constants/about'

type RowProps = {
    term: string
    value: string
    delay: number
}

/**
 * The row owns its own reveal so the rule and the text arrive together, and so
 * the markup stays a valid description list (only dt/dd inside the wrapper).
 */
const ColophonRow: FC<RowProps> = ({ term, value, delay }): ReactNode => {
    const { ref, shown } = useReveal<HTMLDivElement>()

    return (
        <div
            ref={ref}
            data-shown={shown}
            className='fz-reveal relative grid grid-cols-12 gap-x-6 gap-y-2 py-5'
            style={{ transitionDelay: `${delay}s` }}
        >
            <dt className={`col-span-12 sm:col-span-3 ${MICRO_FAINT}`}>
                <span
                    aria-hidden='true'
                    data-shown={shown}
                    className='fz-rule-draw absolute inset-x-0 top-0 block h-px bg-fz-rule'
                    style={{ transitionDelay: `${delay}s` }}
                />

                {term}
            </dt>

            <dd className='col-span-12 text-[15px] leading-relaxed text-fz-sub sm:col-span-9'>
                {value}
            </dd>
        </div>
    )
}

export const Colophon: FC = (): ReactNode => {
    return (
        <div>
            <dl>
                {COLOPHON.map((row, index) => (
                    <ColophonRow
                        key={row.term}
                        term={row.term}
                        value={row.value}
                        delay={index * 0.04}
                    />
                ))}
            </dl>

            <RuleDraw />
        </div>
    )
}
